/** Ten sam okres co generator / backend (`SYNODIC_MONTH_DAYS`). */
export const LUNAR_CYCLE_DAYS = 29.530588853;

const MOON_ICON_BASE = 'https://assets.esoteric.pl/img/fazy-ksiezyca';

/** Połowa cyklu synodycznego — granica przybywający / ubywający. */
const HALF_CYCLE = LUNAR_CYCLE_DAYS / 2;

export const MOON_ICON_FILES = [
  'new-moon.svg',
  'waxing-crescent-1.svg',
  'waxing-crescent-2.svg',
  'waxing-crescent-3.svg',
  'first-quarter.svg',
  'waxing-gibbous-1.svg',
  'waxing-gibbous-2.svg',
  'waxing-gibbous-3.svg',
  'full-moon.svg',
  'waning-gibbous-1.svg',
  'waning-gibbous-2.svg',
  'waning-gibbous-3.svg',
  'last-quarter.svg',
  'waning-crescent-1.svg',
  'waning-crescent-2.svg',
  'waning-crescent-3.svg',
] as const;

/**
 * Normalizuje wiek Księżyca (dni od nowiu) do [0, LUNAR_CYCLE_DAYS).
 */
export function normalizeMoonAge(moonAge: number): number {
  if (!Number.isFinite(moonAge)) {
    return 0;
  }
  let x = moonAge % LUNAR_CYCLE_DAYS;
  if (x < 0) {
    x += LUNAR_CYCLE_DAYS;
  }
  if (x >= LUNAR_CYCLE_DAYS - 1e-9) {
    return 0;
  }
  return x;
}

/**
 * Wybór pliku SVG na podstawie oświetlenia i wieku (waxing vs waning po połowie cyklu).
 * `moon_age` jest normalizowany do jednego cyklu, żeby działało dla dowolnej liczby doby od nowiu.
 */
export function getMoonIcon(illumination: number, moonAge: number): string {
  const ill = Number.isFinite(illumination) ? illumination : 0;
  const age = normalizeMoonAge(moonAge);
  const isWaxing = age < HALF_CYCLE;

  if (ill <= 1) {
    return 'new-moon.svg';
  }
  if (ill >= 95) {
    return 'full-moon.svg';
  }

  if (isWaxing) {
    if (ill < 10) {
      return 'waxing-crescent-1.svg';
    }
    if (ill < 20) {
      return 'waxing-crescent-2.svg';
    }
    if (ill < 35) {
      return 'waxing-crescent-3.svg';
    }
    if (ill < 60) {
      return 'first-quarter.svg';
    }
    if (ill < 70) {
      return 'waxing-gibbous-1.svg';
    }
    if (ill < 80) {
      return 'waxing-gibbous-2.svg';
    }
    return 'waxing-gibbous-3.svg';
  }

  if (ill < 10) {
    return 'waning-crescent-3.svg';
  }
  if (ill < 20) {
    return 'waning-crescent-2.svg';
  }
  if (ill < 35) {
    return 'waning-crescent-1.svg';
  }
  if (ill < 60) {
    return 'last-quarter.svg';
  }
  if (ill < 70) {
    return 'waning-gibbous-3.svg';
  }
  if (ill < 80) {
    return 'waning-gibbous-2.svg';
  }
  return 'waning-gibbous-1.svg';
}

/** @deprecated użyj {@link getMoonIcon} — ta sama sygnatura (illumination, moonAge). */
export function getMoonIconFile(illumination: number, moonAge: number): string {
  return getMoonIcon(illumination, moonAge);
}

export function getMoonIconPath(moonAge: number, illumination: number): string {
  const file = getMoonIcon(illumination, moonAge);
  return `${MOON_ICON_BASE}/${file}`;
}

/**
 * Klatka 0–100 dla wizualizacji CSS (MoonPhaseCodepenStatic / moon_freeze).
 * Ta sama para „połówek” co w animacji: perc 0–50 = prawa strona (przybywa), 51–100 = lewa (ubywa).
 * Mapowanie: przybywający → illumination rośnie wraz z perc (0→50); ubywający → lustrzanie (perc 50→100 przy spadku illumination).
 */
export function illuminationToMoonFreezePerc(illumination: number, moonAge: number): number {
  const ill = Math.min(100, Math.max(0, Number.isFinite(illumination) ? illumination : 0));
  const age = normalizeMoonAge(moonAge);
  const isWaxing = age < HALF_CYCLE;
  if (isWaxing) {
    return Math.min(50, Math.max(0, Math.round(ill / 2)));
  }
  return Math.min(100, Math.max(50, Math.round(100 - ill / 2)));
}
